import React, { useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { motion } from "framer-motion";
import PersonAdd from "@mui/icons-material/PersonAdd";
import { LoadingButton } from "@mui/lab";
import {
  PhoneCountryCodeMap,
  combinePhoneNumber,
  isValidPhoneNumberForCountry,
  getDefaultCountryCode,
  API_RESPONSE_CODE,
} from "@kivunova/kivufrontendcommon";
import { CountryCode } from "libphonenumber-js";
import {
  fullWidth,
  formMaxWidth,
  responsiveHeading,
} from "../../styles/layoutStyles";
import { useCreateSecondarySuperAdmin } from "../../hooks/api/useAdminAuth";

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const AddAdminUserPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState(() =>
    getDefaultCountryCode(),
  );
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const createMutation = useCreateSecondarySuperAdmin();

  const validatePhone = useCallback(
    (number: string, countryCodeKey: string): boolean => {
      if (!number.trim()) {
        setPhoneError("");
        return true;
      }
      if (!countryCodeKey) {
        setPhoneError("Select a country code.");
        return false;
      }
      const codeValue = PhoneCountryCodeMap[countryCodeKey]?.code;
      if (!codeValue) {
        setPhoneError("Invalid country code.");
        return false;
      }
      const isValid = isValidPhoneNumberForCountry(
        number,
        countryCodeKey as CountryCode,
        codeValue,
      );
      if (!isValid) {
        setPhoneError("Invalid phone number for the selected country.");
        return false;
      }
      setPhoneError("");
      return true;
    },
    [],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    const hasPhone = phoneNumber.trim().length > 0;
    if (hasPhone && !validatePhone(phoneNumber, phoneCountryCode)) {
      return;
    }

    const phone = hasPhone
      ? combinePhoneNumber(
          phoneCountryCode,
          phoneNumber.trim(),
          PhoneCountryCodeMap,
        )
      : undefined;

    createMutation.mutate(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone ?? null,
      },
      {
        onSuccess: (data) => {
          const res = data.createSecondarySuperAdmin;
          if (res.code === API_RESPONSE_CODE.OK) {
            setMessage({
              type: "success",
              text: `Secondary Super Admin created. Credentials have been sent to ${res.user?.email ?? email}.`,
            });
            setFirstName("");
            setLastName("");
            setEmail("");
            setPhoneNumber("");
          } else {
            setMessage({
              type: "error",
              text: res.code ?? "Failed to create user.",
            });
          }
        },
        onError: () => {
          setMessage({
            type: "error",
            text: "Failed to create user. Please try again.",
          });
        },
      },
    );
  };

  return (
    <Box
      sx={{
        ...fullWidth,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
      }}
    >
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        style={{ width: "100%", maxWidth: formMaxWidth }}
      >
        <Typography variant="h5" sx={{ mb: 3, ...responsiveHeading }}>
          Add Admin User
        </Typography>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            width: "100%",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {message && (
              <Alert
                severity={message.type}
                sx={{ mb: 2 }}
                onClose={() => setMessage(null)}
              >
                {message.text}
              </Alert>
            )}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <TextField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
              />
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <FormControl
                  sx={{ minWidth: 140 }}
                  error={Boolean(phoneError)}
                  size="medium"
                >
                  <InputLabel id="phone-country-code-label">
                    Country code
                  </InputLabel>
                  <Select
                    labelId="phone-country-code-label"
                    value={phoneCountryCode}
                    label="Country code"
                    onChange={(e) => {
                      setPhoneCountryCode(e.target.value);
                      setPhoneError("");
                    }}
                  >
                    {Object.keys(PhoneCountryCodeMap).map((key) => (
                      <MenuItem key={key} value={key}>
                        {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Phone"
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                    if (phoneError) setPhoneError("");
                  }}
                  error={Boolean(phoneError)}
                  helperText={phoneError}
                  fullWidth
                  sx={{ flex: 1, minWidth: 160 }}
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                startIcon={<PersonAdd />}
                sx={{ alignSelf: "flex-start", textTransform: "none", mt: 1 }}
                disabled={createMutation.isPending}
                component={LoadingButton}
                loading={createMutation.isPending}
              >
                Add user
              </Button>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default AddAdminUserPage;
